/**
 * COMPONENT_SPEC §5 — the commission. OWNER: web-frontend-engineer.
 *
 * `OptionColumn` is THE component RULING-005 turns on: it is rendered
 * identically whether the hero is the 3D mirror or the written specification.
 * There is one of it. The two paths cannot drift because there is nothing to
 * drift from.
 *
 * Nothing here computes selectability, price, metreage or a rule outcome. Every
 * one of those arrives from `API-CONFIG-EVAL` and is rendered as given
 * (FLOWS_SPEC §2, structural decision 2).
 */

import { useRef } from 'react';
import type { NotApplicableOption, OptionState, PriceLine, Quote } from '@pt/core';
import { t, tDynamic, type CopyKey } from '@pt/copy';
import { GARMENT_HEADING, OPTION_GROUPS, optionLabel, optionValueLabel } from '../lib/optionCopy.ts';
import { anyPence, decimal, money } from '@pt/view';
import { Button, FactLine, Link, SelectionMark, Statement, TextInput } from './primitives.tsx';

/* ------------------------------------------------------------ PriceDisplay */

export type PriceState = 'current' | 'recomputing' | 'failed' | 'unavailable';

export interface PriceDisplayProps {
  readonly state: PriceState;
  readonly quote: Quote | null;
  readonly onRetry?: () => void;
  readonly onOpenBreakdown?: () => void;
  readonly breakdownOpen?: boolean;
  readonly onGoToOption?: (optionId: string) => void;
  readonly tailorName: string;
}

/**
 * `EL-PRICE`. One position in the configurator and one on Review, and NOWHERE
 * ELSE in the client UI: there is never a second number on screen that could
 * also be "the price".
 *
 * Changes in place. No animation, no count-up, no flash, no pulse, no colour.
 * The reserved line beneath means the figure never moves.
 */
export function PriceDisplay({
  state,
  quote,
  onRetry,
  onOpenBreakdown,
  breakdownOpen = false,
  onGoToOption,
  tailorName,
}: PriceDisplayProps) {
  const held = useRef<Quote | null>(null);
  if (quote !== null) held.current = quote;
  const shown = state === 'recomputing' ? (quote ?? held.current) : quote;
  const pence = shown === null ? false : anyPence(shown.lines.map((l) => l.amountMinor));

  return (
    <section className="pt-price" aria-label={t('el.price.what')}>
      <h2 className="pt-price__heading">{t('el.price.what')}</h2>

      {state === 'unavailable' || shown === null ? (
        <FactLine>{t('el.price.unavailable')}</FactLine>
      ) : state === 'failed' ? (
        <>
          <FactLine>{t('el.price.failed')}</FactLine>
          <div className="pt-actions">
            <Button onClick={onRetry}>{t('el.price.failed.action')}</Button>
          </div>
        </>
      ) : (
        <>
          <p className="pt-price__total" aria-live="polite">
            {money(shown.totalMinor, shown.currency, pence)}
          </p>
          {/* Reserved height so the figure never moves (KEY-NOCLS). */}
          <p className="pt-price__reserve">
            {state === 'recomputing' ? t('el.price.recomputing') : ''}
          </p>
          <FactLine>{t('el.price.inclusions')}</FactLine>
          <FactLine>{t('el.price.unknowns')}</FactLine>
          <FactLine>{t('el.price.process', { tailorName })}</FactLine>
          <div className="pt-actions">
            <Button onClick={onOpenBreakdown}>{t('el.price.breakdown')}</Button>
          </div>
          {breakdownOpen ? (
            <PriceBreakdown quote={shown} pence={pence} {...(onGoToOption ? { onGoToOption } : {})} />
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Every line names the choice that caused it and links back to that control
 * ([T-51]). Cloth and making are separate lines. There is no "service charge",
 * no "handling", no rounding line, and no line the client did not choose.
 */
export function PriceBreakdown({
  quote,
  pence,
  onGoToOption,
}: {
  quote: Quote;
  pence: boolean;
  onGoToOption?: (optionId: string) => void;
}) {
  return (
    <table className="pt-price__table">
      <caption className="pt-visually-hidden">{t('review.breakdown.heading')}</caption>
      <tbody>
        {quote.lines.map((line, index) => (
          <tr key={`${line.kind}-${line.optionId ?? index}`}>
            <td>
              <span className="pt-price__row">
                <span>{breakdownLabel(line, quote)}</span>
                <span className="pt-price__amount">
                  {money(line.amountMinor, quote.currency, pence)}
                </span>
              </span>
              {line.optionId != null && onGoToOption !== undefined ? (
                <FactLine>
                  <Link
                    href={`#option-${line.optionId}`}
                    onNavigate={() => onGoToOption(line.optionId as string)}
                  >
                    {t('review.breakdown.backToControl', { optionLabel: optionLabel(line.optionId) })}
                  </Link>
                </FactLine>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function breakdownLabel(line: PriceLine, quote: Quote): string {
  if (line.kind === 'cloth') {
    return t('review.breakdown.cloth', {
      clothName: line.labelText,
      metreage: decimal(quote.clothRequirementMetres, 1),
      amountGBP: money(line.amountMinor, quote.currency),
    });
  }
  if (line.kind === 'base') {
    return t('review.breakdown.making', {
      commissionTypeLabel: line.labelText,
      amountGBP: money(line.amountMinor, quote.currency),
    });
  }
  if (line.forcedByConstraintId != null) {
    return t('review.breakdown.forced', {
      valueLabel: line.labelText,
      reason: tDynamic(`config.reason.${line.forcedByConstraintId}`),
      amountGBP: money(line.amountMinor, quote.currency),
    });
  }
  return t('review.breakdown.choice', {
    valueLabel: line.labelText,
    optionLabel: line.optionId == null ? '' : optionLabel(line.optionId),
    amountGBP: money(line.amountMinor, quote.currency),
  });
}

/* ------------------------------------------------------------- OptionGroup */

export interface OptionGroupProps {
  readonly option: OptionState;
  readonly onChange: (optionId: string, valueId: string | number | boolean) => void;
  readonly busy?: boolean;
  /** The price consequence of a value, at the point of the choice (M-10). */
  readonly priceNote?: (valueId: string | number | boolean) => string | null;
}

/**
 * One option, as a radiogroup with roving tabindex. `ST-CONFIG-VALUE-
 * UNAVAILABLE`: the value STAYS LISTED IN PLACE, carries `aria-disabled`,
 * remains arrow-reachable, and its reason is visible beside it AND in its
 * accessible name. It is never removed and never selectable-then-rejected.
 *
 * `hard` and `restricted` are presented identically. The copy never mentions
 * that a tailor could release a rule — the configurator is not a negotiation.
 */
export function OptionGroup({ option, onChange, busy = false, priceNote }: OptionGroupProps) {
  const label = optionLabel(option.optionId);
  const groupId = `option-${option.optionId}`;

  if (option.type === 'number' && option.numericRange !== null) {
    return (
      <NumericOption option={option} onChange={onChange} busy={busy} />
    );
  }
  if (option.type === 'text') {
    return <MonogramOption option={option} onChange={onChange} />;
  }

  const selectedIndex = option.values.findIndex((v) => v.valueId === option.currentValue);
  return (
    <div className="pt-optiongroup" id={groupId}>
      <div className="pt-optiongroup__label" id={`${groupId}-label`}>
        {label}
      </div>
      <div role="radiogroup" aria-labelledby={`${groupId}-label`} aria-busy={busy ? true : undefined}>
        {option.values.map((value, index) => {
          const chosen = value.valueId === option.currentValue;
          const valueLabel = optionValueLabel(option.optionId, value.valueId);
          const reason = reasonFrom(value.reasonCopyKey, value.reasonText, label);
          const price = priceNote?.(value.valueId) ?? null;
          const accessibleName = value.selectable
            ? chosen
              ? t('ctl.selected.aria', { valueLabel })
              : valueLabel
            : t('ctl.unavailable.aria', { valueLabel, reason: reason ?? '' });
          return (
            <button
              key={String(value.valueId)}
              type="button"
              role="radio"
              className="pt-option"
              aria-checked={chosen}
              aria-disabled={value.selectable ? undefined : true}
              aria-label={accessibleName}
              tabIndex={
                selectedIndex === -1 ? (index === 0 ? 0 : -1) : chosen ? 0 : -1
              }
              onKeyDown={(event) => rove(event, option, index, onChange)}
              onClick={() => {
                if (!value.selectable) return;
                onChange(option.optionId, value.valueId);
              }}
            >
              <SelectionMark checked={chosen} radio />
              <span className="pt-option__body">
                <span className="pt-option__label">{valueLabel}</span>
                {reason === null || value.selectable ? null : (
                  <span className="pt-option__reason">{reason}</span>
                )}
                {price === null ? null : <span className="pt-option__price">{price}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The written reason a value or an option cannot be taken. The server's own
 * craft sentence (`reasonText`) is preferred; where it has none it hands us a
 * copy key. `error.CONSTRAINT_VIOLATION` needs both `{optionLabel}` and
 * `{reason}` and we can only ever supply the label here, so the key degrades to
 * its slotless twin (`.generic`) inside `tDynamic` — an unfilled slot must never
 * crash the configurator (clarity Finding 1), which is the very screen whose job
 * is to make "you can't have that" clear.
 */
function reasonFrom(reasonCopyKey: string | null, reasonText: string | null, forLabel: string): string | null {
  if (reasonText != null && reasonText !== '') return reasonText;
  if (reasonCopyKey == null) return null;
  return tDynamic(reasonCopyKey, { optionLabel: forLabel });
}

/**
 * Arrow keys move within the group and UNAVAILABLE VALUES STAY ARROW-REACHABLE
 * — that is the whole point of `aria-disabled` over `disabled`.
 */
function rove(
  event: React.KeyboardEvent<HTMLButtonElement>,
  option: OptionState,
  index: number,
  onChange: (optionId: string, valueId: string | number | boolean) => void,
): void {
  const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
  if (!keys.includes(event.key)) return;
  event.preventDefault();
  const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
  const next = (index + (forward ? 1 : -1) + option.values.length) % option.values.length;
  const target = event.currentTarget.parentElement?.children[next];
  if (target instanceof HTMLElement) target.focus();
  const value = option.values[next];
  if (value !== undefined && value.selectable) onChange(option.optionId, value.valueId);
}

/** `ST-CONFIG-NUMERIC` — the legal range is computed live by the server. */
function NumericOption({
  option,
  onChange,
  busy,
}: {
  option: OptionState;
  onChange: (optionId: string, valueId: string | number | boolean) => void;
  busy: boolean;
}) {
  const range = option.numericRange;
  if (range === null) return null;
  const current = typeof option.currentValue === 'number' ? option.currentValue : range.min;
  const set = (next: number) => {
    const clamped = Math.min(range.max, Math.max(range.min, Number(next.toFixed(1))));
    onChange(option.optionId, clamped);
  };
  return (
    <div className="pt-optiongroup" id={`option-${option.optionId}`}>
      <div className="pt-optiongroup__label">{optionLabel(option.optionId)}</div>
      <div
        role="spinbutton"
        tabIndex={0}
        aria-valuemin={range.min}
        aria-valuemax={range.max}
        aria-valuenow={current}
        aria-label={optionLabel(option.optionId)}
        aria-busy={busy ? true : undefined}
        className="pt-option"
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
            event.preventDefault();
            set(current + range.step);
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
            event.preventDefault();
            set(current - range.step);
          }
          if (event.key === 'PageUp') {
            event.preventDefault();
            set(current + range.step * 5);
          }
          if (event.key === 'PageDown') {
            event.preventDefault();
            set(current - range.step * 5);
          }
        }}
      >
        <span className="pt-option__body">
          <span className="pt-option__label">{decimal(current, 1)}</span>
          <span className="pt-option__reason">{t('config.numeric.hem.help')}</span>
          <span className="pt-option__reason">
            {t('config.numeric.hem.floor', { minHemCm: decimal(range.min, 1) })}
          </span>
        </span>
      </div>
    </div>
  );
}

/** `ST-CONFIG-MONOGRAM` — disallowed characters are refused AT INPUT. */
function MonogramOption({
  option,
  onChange,
}: {
  option: OptionState;
  onChange: (optionId: string, valueId: string | number | boolean) => void;
}) {
  const value = typeof option.currentValue === 'string' ? option.currentValue : '';
  const pattern = /^[A-Za-z][A-Za-z. ]{0,5}$/;
  return (
    <div className="pt-optiongroup" id={`option-${option.optionId}`}>
      <TextInput
        label={optionLabel(option.optionId)}
        value={value}
        hint={t('config.monogram.help')}
        onChange={(next) => {
          if (next === '' || pattern.test(next)) onChange(option.optionId, next);
        }}
      />
      <FactLine>{t('config.monogram.count', { used: value.length })}</FactLine>
    </div>
  );
}

/* ------------------------------------------------------------ OptionColumn */

export interface OptionColumnProps {
  readonly options: readonly OptionState[];
  readonly notApplicable: readonly NotApplicableOption[];
  readonly onChange: (optionId: string, valueId: string | number | boolean) => void;
  readonly busyOptionId: string | null;
  readonly warnings: readonly { readonly constraintId: string; readonly text: string }[];
}

/**
 * ONE column of decisions, grouped per garment in the order the workshop works.
 * THE SAME COMPONENT under both heroes (RULING-005).
 *
 * The three ways an option can be unreachable, and why they look different:
 *   · `ST-CONFIG-NA-GARMENT`   — the option is not in `options[]` at all, so
 *     nothing renders and nothing is said. Silent absence.
 *   · `ST-CONFIG-NA-DERIVED`   — the option is in `notApplicable[]`: the
 *     control WITHDRAWS and is replaced, in the same slot, by a `Statement`
 *     giving the craft reason.
 *   · `ST-CONFIG-VALUE-UNAVAILABLE` — handled inside `OptionGroup`.
 */
export function OptionColumn({
  options,
  notApplicable,
  onChange,
  busyOptionId,
  warnings,
}: OptionColumnProps) {
  const byId = new Map(options.map((o) => [o.optionId, o]));
  const naById = new Map(notApplicable.map((n) => [n.optionId, n]));
  const garments = ['commission', 'jacket', 'trousers', 'waistcoat'] as const;

  return (
    <div className="pt-config__column">
      {/* ST-CONFIG-WARN — a quiet, honest note. Never blocks, never styled as
          pressure, scarcity or an alert. Same field as every other fact.
          `API-CONFIG-EVAL` returns no optionId on a warning, so they render as
          one quiet block rather than beside a control we would have to guess
          at; recorded for the backend engineer in the round report. */}
      {warnings.length === 0 ? null : (
        <div className="pt-block">
          {warnings.map((warning) => (
            <FactLine key={warning.constraintId}>{warning.text}</FactLine>
          ))}
        </div>
      )}
      {garments.map((garment) => {
        const groups = OPTION_GROUPS.filter((g) => g.garment === garment).filter((g) =>
          g.optionIds.some((id) => byId.has(id) || naById.has(id)),
        );
        if (groups.length === 0) return null;
        const heading = GARMENT_HEADING[garment];
        return (
          <section key={garment} className="pt-section">
            {heading === undefined ? null : (
              <h2 className="pt-record__groupHeading">{t(heading)}</h2>
            )}
            {groups.map((group) => (
              <div key={`${garment}-${group.headingKey}`} className="pt-block">
                <h3 className="pt-optiongroup__label">{t(group.headingKey)}</h3>
                {group.optionIds.map((optionId) => {
                  const na = naById.get(optionId);
                  if (na !== undefined) {
                    // ST-CONFIG-NA-DERIVED: a control seen a moment ago must not
                    // vanish unexplained. It is replaced, in its own slot.
                    return (
                      <div key={optionId} className="pt-optiongroup" id={`option-${optionId}`}>
                        <div className="pt-optiongroup__label">{optionLabel(optionId)}</div>
                        <Statement tone="field">
                          {reasonFrom(na.reasonCopyKey, na.reasonText, optionLabel(optionId)) ?? ''}
                        </Statement>
                      </div>
                    );
                  }
                  const option = byId.get(optionId);
                  // ST-CONFIG-NA-GARMENT: silent absence. No note, no slot.
                  if (option === undefined) return null;
                  return (
                    <OptionGroup
                      key={optionId}
                      option={option}
                      onChange={onChange}
                      busy={busyOptionId === optionId}
                      priceNote={(valueId) => canvasPriceNote(optionId, valueId)}
                    />
                  );
                })}
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

/**
 * `jacket.canvas` states both values' price consequence at the point of the
 * choice, in the tailor's honest terms. A reduction is stated as the plain fact
 * with the money — never "save £250", never styled as an offer.
 *
 * The AMOUNT comes from the quote's own modifier lines, so nothing here holds a
 * price. Until a quote exists there is no amount to state and the note is
 * absent rather than invented.
 */
let canvasAmounts: Readonly<Record<string, string>> = {};
export function setCanvasAmounts(amounts: Readonly<Record<string, string>>): void {
  canvasAmounts = amounts;
}
function canvasPriceNote(optionId: string, valueId: string | number | boolean): string | null {
  if (optionId !== 'jacket.canvas') return null;
  const amount = canvasAmounts[String(valueId)];
  if (valueId === 'full') return t('config.canvas.full');
  if (amount === undefined) return null;
  if (valueId === 'half') return t('config.canvas.half', { amountGBP: amount });
  if (valueId === 'fused') return t('config.canvas.fused', { amountGBP: amount });
  return null;
}

/* ------------------------------------------------------------ TwoPathChoice */

export interface TwoPathOption {
  readonly id: string;
  readonly label: string;
}

export interface TwoPathChoiceProps {
  readonly headingKey?: CopyKey;
  readonly heading?: string;
  readonly statement: string;
  readonly options: readonly [TwoPathOption, TwoPathOption];
  readonly chosen: string | null;
  readonly onChoose: (id: string) => void;
  readonly describedById?: string;
}

/**
 * RULING-008. Two equal paths, NEITHER PRE-SELECTED, swatch-first listed first.
 *
 * It has no alert affordance and is structurally incapable of acquiring one:
 * no `color.alert.*` may render on this screen, `color.alert.surface` and
 * `color.alert.border` DO NOT EXIST as tokens, no warning glyph exists, and the
 * block has no border, tint, left rule, fill or container.
 */
export function TwoPathChoice({
  headingKey,
  heading,
  statement,
  options,
  chosen,
  onChoose,
  describedById,
}: TwoPathChoiceProps) {
  const statementId = describedById ?? 'pt-twopath-statement';
  return (
    <section className="pt-twopath">
      <h2 className="pt-cutsheet__blockTitle">{headingKey === undefined ? heading : t(headingKey)}</h2>
      <p className="pt-statement pt-statement--region" id={statementId}>
        {statement}
      </p>
      <div role="radiogroup" aria-describedby={statementId}>
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            role="radio"
            className="pt-twopath__option"
            aria-checked={chosen === option.id}
            onClick={() => onChoose(option.id)}
          >
            <SelectionMark checked={chosen === option.id} radio />
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
